import React, { useState, useMemo, useRef } from "react";
import api, { formatDateTime, formatTL } from "@/lib/api";
import { useLiveQuery, TAGS } from "@/lib/liveSync";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plate } from "@/components/Plate";
import { toast } from "sonner";
import { Trash, Plus, Warning, Money, CreditCard, Bank, MagnifyingGlass, PencilSimple, UploadSimple, User, Phone } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const METHODS = [
  { key: "cash", label: "Nakit", icon: Money },
  { key: "card", label: "Kredi Kartı", icon: CreditCard },
  { key: "bank_transfer", label: "EFT / Havale", icon: Bank },
];

export default function PlateSubscriptions() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [open, setOpen] = useState(false);
  const emptyForm = {
    plate: "", plan_id: "",
    start_date: "", end_date: "",
    record_payment: true,
    payment_method: "cash",
    amount: "",
    payment_note: "",
    customer_name: "",
    customer_phone: "",
  };
  const [form, setForm] = useState(emptyForm);
  // Sahip düzenleme modal state
  const [editing, setEditing] = useState(null);  // {id, plate, customer_name, customer_phone}
  const [editBusy, setEditBusy] = useState(false);
  // Arama + CSV
  const [search, setSearch] = useState("");
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const csvInputRef = useRef(null);

  const { data: items } = useLiveQuery(
    TAGS.PLATE_SUBSCRIPTIONS,
    async () => (await api.get("/plate-subscriptions")).data,
  );
  const { data: plans } = useLiveQuery(
    TAGS.PLANS,
    async () => (await api.get("/plans")).data,
  );
  const { data: expiring } = useLiveQuery(
    [TAGS.PLATE_SUBSCRIPTIONS, TAGS.USERS],
    async () => (await api.get("/plate-subscriptions/expiring")).data,
    { intervalMs: 30000 },
  );

  const onPlanChange = (v) => {
    const p = (plans || []).find((x) => x.id === v);
    if (!p) return setForm({ ...form, plan_id: v });
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + Number(p.duration_days || 30));
    setForm({
      ...form,
      plan_id: v,
      amount: form.amount || String(p.price || ""),
      start_date: start.toISOString().slice(0, 10) + "T00:00:00+00:00",
      end_date: end.toISOString().slice(0, 10) + "T23:59:59+00:00",
    });
  };

  const create = async () => {
    try {
      const payload = {
        plate: form.plate,
        plan_id: form.plan_id,
        start_date: form.start_date,
        end_date: form.end_date,
        record_payment: !!form.record_payment,
        payment_method: form.payment_method || "cash",
        amount: form.amount === "" ? null : Number(form.amount),
        payment_note: form.payment_note || null,
        customer_name: form.customer_name || null,
        customer_phone: form.customer_phone || null,
      };
      const { data } = await api.post("/plate-subscriptions", payload);
      const renewMsg = data.renewed
        ? `Mevcut aktif abonelik uzatıldı — Yeni bitiş: ${(data.end_date || "").slice(0,10)}`
        : "Yeni abonelik oluşturuldu";
      const payMsg = form.record_payment
        ? ` — Kasa'ya ${METHODS.find(m => m.key === form.payment_method)?.label || form.payment_method} olarak yansıdı`
        : " — Ödeme kaydedilmedi";
      toast.success(renewMsg + payMsg);
      setOpen(false);
      setForm(emptyForm);
    } catch (e) { toast.error(e?.response?.data?.detail || "Oluşturulamadı"); }
  };

  const saveCustomerEdit = async () => {
    if (!editing) return;
    setEditBusy(true);
    try {
      await api.patch(`/plate-subscriptions/${editing.id}/customer`, {
        customer_name: editing.customer_name || null,
        customer_phone: editing.customer_phone || null,
      });
      toast.success("Müşteri bilgisi güncellendi");
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    } finally { setEditBusy(false); }
  };

  const onCsvSelected = async (file) => {
    if (!file) return;
    setCsvBusy(true);
    setCsvResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/plate-subscriptions/import-customers", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCsvResult(data);
      toast.success(`${data.updated} kayıt güncellendi${data.not_found_count > 0 ? `, ${data.not_found_count} plaka bulunamadı` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CSV yüklenemedi");
    } finally {
      setCsvBusy(false);
      if (csvInputRef.current) csvInputRef.current.value = "";
    }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    try { await api.delete(`/plate-subscriptions/${id}`); toast.success("Silindi"); }
    catch { toast.error("Silinemedi"); }
  };

  // Filter items by search (plaka VEYA sahip adı)
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items || [];
    return (items || []).filter((it) => {
      const plate = (it.plate || "").toLowerCase();
      const name = (it.customer_name || "").toLowerCase();
      const phone = (it.customer_phone || "").toLowerCase();
      const corp = (it.corporate_name || "").toLowerCase();
      return plate.includes(q) || name.includes(q) || phone.includes(q) || corp.includes(q);
    });
  }, [items, search]);

  const expObj = expiring || { plate_subscriptions: [], users: [] };
  const totalExpiring = expObj.plate_subscriptions.length + expObj.users.length;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Aboneler</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Plaka Abonelikleri</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* CSV toplu müşteri yükleme (admin) */}
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            data-testid="plate-sub-csv-input"
            onChange={(e) => onCsvSelected(e.target.files?.[0])}
          />
          <Button
            variant="outline"
            onClick={() => csvInputRef.current?.click()}
            disabled={csvBusy}
            className="rounded-sm uppercase tracking-widest text-xs font-semibold"
            data-testid="plate-sub-csv-btn"
            title="Toplu müşteri adı/telefonu yüklemek için CSV seç (plaka,isim,telefon)"
          >
            <UploadSimple size={14} /> {csvBusy ? "Yükleniyor..." : "CSV ile Müşteri Yükle"}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="plate-sub-add-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
              <Plus size={16} /> Yeni Abonelik
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Plakaya Abonelik Ekle</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Plaka</Label>
                <Input value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })} className="rounded-sm font-plate uppercase" data-testid="plate-sub-plate" />
              </div>
              <div>
                <Label>Plan</Label>
                <Select value={form.plan_id} onValueChange={onPlanChange}>
                  <SelectTrigger className="rounded-sm" data-testid="plate-sub-plan"><SelectValue placeholder="Plan seç..." /></SelectTrigger>
                  <SelectContent>
                    {(plans || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — ₺{p.price} / {p.duration_days}gün</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Başlangıç</Label>
                  <Input type="date" value={form.start_date.slice(0, 10)} onChange={(e) => setForm({ ...form, start_date: e.target.value + "T00:00:00+00:00" })} className="rounded-sm" data-testid="plate-sub-start" />
                </div>
                <div>
                  <Label>Bitiş</Label>
                  <Input type="date" value={form.end_date.slice(0, 10)} onChange={(e) => setForm({ ...form, end_date: e.target.value + "T23:59:59+00:00" })} className="rounded-sm" data-testid="plate-sub-end" />
                </div>
              </div>

              {/* Müşteri bilgisi — opsiyonel ama önerilir */}
              <div className="p-3 rounded-sm border border-border bg-muted/30 space-y-3">
                <div className="text-xs uppercase tracking-widest font-semibold flex items-center gap-1">
                  <User size={13} /> Müşteri Bilgisi <span className="text-muted-foreground normal-case tracking-normal text-[10px]">(opsiyonel)</span>
                </div>
                <div>
                  <Label className="text-xs">Sahip Adı Soyadı</Label>
                  <Input
                    value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    className="rounded-sm h-9"
                    placeholder="Örn. Ahmet Yılmaz"
                    data-testid="plate-sub-customer-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Telefon</Label>
                  <Input
                    value={form.customer_phone}
                    onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                    className="rounded-sm h-9 font-mono"
                    placeholder="05XX XXX XX XX"
                    data-testid="plate-sub-customer-phone"
                  />
                </div>
              </div>

              {/* Payment section — admin controls whether Kasa is credited */}
              <div className="border-t border-border pt-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold uppercase tracking-widest">Ödeme Alındı mı?</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Kapatırsanız abonelik oluşur ama Kasa&apos;ya yansımaz (sonra &quot;Abonelik Ödemeleri&quot; sayfasından alabilirsiniz).
                    </div>
                  </div>
                  <Switch
                    checked={form.record_payment}
                    onCheckedChange={(v) => setForm({ ...form, record_payment: v })}
                    data-testid="plate-sub-record-payment"
                  />
                </div>

                {form.record_payment && (
                  <div className="p-3 rounded-sm border border-primary/30 bg-primary/5 space-y-3">
                    <div>
                      <Label>Ödeme Yöntemi</Label>
                      <div className="grid grid-cols-3 gap-2 mt-1">
                        {METHODS.map((m) => {
                          const Icon = m.icon;
                          const active = form.payment_method === m.key;
                          return (
                            <button
                              key={m.key}
                              type="button"
                              onClick={() => setForm({ ...form, payment_method: m.key })}
                              data-testid={`plate-sub-method-${m.key}`}
                              className={`p-2 rounded-sm border-2 flex flex-col items-center gap-1 transition ${
                                active ? "border-primary bg-primary/10" : "border-border hover:border-muted-foreground"
                              }`}
                            >
                              <Icon size={20} weight={active ? "fill" : "regular"}
                                    className={active ? "text-primary" : "text-muted-foreground"} />
                              <span className={`text-[10px] uppercase tracking-widest font-semibold ${active ? "text-primary" : ""}`}>
                                {m.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <Label>Tahsil Edilecek Tutar (₺)</Label>
                      <Input type="number" step="0.01" value={form.amount}
                             onChange={(e) => setForm({ ...form, amount: e.target.value })}
                             className="rounded-sm font-mono"
                             placeholder="Plan fiyatı (boş bırakırsanız otomatik)"
                             data-testid="plate-sub-amount" />
                    </div>
                    <div>
                      <Label className="text-xs">Not (opsiyonel)</Label>
                      <Input value={form.payment_note}
                             onChange={(e) => setForm({ ...form, payment_note: e.target.value })}
                             className="rounded-sm h-9" placeholder="Referans no / açıklama..."
                             data-testid="plate-sub-note" />
                    </div>
                  </div>
                )}
                {!form.record_payment && (
                  <div className="p-3 rounded-sm border border-amber-300 bg-amber-50">
                    <div className="text-xs text-amber-800">
                      ⚠ Abonelik oluşacak, <b>Kasa&apos;ya ödeme kaydı düşmeyecek</b>. Ödemeyi daha sonra
                      &quot;Abonelik Ödemeleri&quot; sayfasından ekleyin.
                    </div>
                  </div>
                )}
              </div>

              <Button data-testid="plate-sub-create-btn" onClick={create} className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
                Oluştur
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* CSV sonuç raporu */}
      {csvResult && (
        <Card className="rounded-sm border-blue-300 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="text-xs text-blue-900">
                <div className="font-semibold uppercase tracking-widest mb-1">CSV Yükleme Sonucu</div>
                <div>✓ <b>{csvResult.updated}</b> abonelik güncellendi</div>
                {csvResult.not_found_count > 0 && (
                  <div className="mt-1">
                    ⚠ <b>{csvResult.not_found_count}</b> plaka veritabanında bulunamadı (abonelik açılmamış olabilir):
                    <div className="font-mono text-[10px] mt-1 max-h-16 overflow-y-auto opacity-80">
                      {(csvResult.not_found_plates || []).join(", ")}
                    </div>
                  </div>
                )}
                {(csvResult.errors || []).length > 0 && (
                  <div className="mt-1 text-red-800">
                    Hatalar: {csvResult.errors.join("; ")}
                  </div>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={() => setCsvResult(null)} className="text-xs">Kapat</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Arama */}
      <Card className="rounded-sm border-border">
        <CardContent className="p-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Plaka veya sahip adına göre ara — örn. 34ABC veya Ahmet"
              className="rounded-sm pl-9 h-10"
              data-testid="plate-sub-search"
            />
          </div>
        </CardContent>
      </Card>

      {totalExpiring > 0 && (
        <Card className="rounded-sm border-amber-400 bg-amber-50">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Warning size={20} weight="fill" className="text-amber-600" />
              <h3 className="font-semibold uppercase tracking-widest text-sm text-amber-800">
                Süresi Yaklaşan / Biten Abonelikler ({totalExpiring})
              </h3>
            </div>
            <div className="space-y-2">
              {expObj.plate_subscriptions.map((s) => (
                <div key={s.id} className="flex items-center justify-between p-2 bg-white rounded-sm">
                  <div className="flex items-center gap-2">
                    <Plate>{s.plate}</Plate>
                    <span className="text-xs text-muted-foreground">{s.plan_name}</span>
                  </div>
                  <div className="text-xs">
                    {/* Feb 2026: subscription artık `end_date` ile sonlanmaz —
                        bu panel yalnızca "ödeme almayı hatırla" göstergesidir. */}
                    <span className={s.expired ? "text-amber-800 font-semibold" : "text-amber-700"}>
                      {s.expired ? "ÖDEME ALINMALI" : "Yakında ödeme günü"}
                    </span>
                    <span className="ml-2 text-muted-foreground">{formatDateTime(s.end_date)}</span>
                  </div>
                </div>
              ))}
              {expObj.users.map((u) => (
                <div key={u.id} className="flex items-center justify-between p-2 bg-white rounded-sm">
                  <div className="flex items-center gap-2">
                    {u.plate && <Plate>{u.plate}</Plate>}
                    <span className="text-xs">{u.name}</span>
                    <span className="text-xs text-muted-foreground">({u.email})</span>
                  </div>
                  <div className="text-xs">
                    <span className={u.expired ? "text-amber-800 font-semibold" : "text-amber-700"}>
                      {u.expired ? "ÖDEME ALINMALI" : "Yakında ödeme günü"}
                    </span>
                    <span className="ml-2 text-muted-foreground">{formatDateTime(u.subscription_expires)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-sm border-border">
        <CardContent className="p-0">
          <div className="p-3 text-xs text-muted-foreground border-b border-border">
            {search ? (
              <>Gösterilen: <b>{filteredItems.length}</b> / Toplam: <b>{(items || []).length}</b></>
            ) : (
              <>Toplam abonelik: <b>{(items || []).length}</b></>
            )}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plaka</TableHead>
                <TableHead>Sahip</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Başlangıç</TableHead>
                <TableHead>Bitiş</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {search ? `"${search}" ile eşleşen kayıt yok` : "Abonelik yok"}
                  </TableCell>
                </TableRow>
              )}
              {filteredItems.map((it) => {
                const nowIso = new Date().toISOString();
                // Feb 2026: Abonelik `end_date` ile bitmez, sadece super_admin
                // manuel silerek biter. Bu yüzden active = `it.active` (start
                // gelecek tarihli değilse) yeterli; "expired" badge kaldırıldı.
                const active = it.active && it.start_date <= nowIso;
                const pending = it.active && it.start_date > nowIso;
                return (
                  <TableRow key={it.id} data-testid={`plate-sub-row-${it.plate}`}>
                    <TableCell>
                      <Plate>{it.plate}</Plate>
                      {it.corporate_name && (
                        <div className="text-[9px] mt-1 text-primary uppercase tracking-widest font-semibold">
                          🏢 {it.corporate_name}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {it.customer_name ? (
                        <div>
                          <div className="text-sm font-medium flex items-center gap-1" data-testid={`plate-sub-customer-${it.plate}`}>
                            <User size={12} className="text-muted-foreground" />
                            {it.customer_name}
                          </div>
                          {it.customer_phone && (
                            <div className="text-xs text-muted-foreground font-mono flex items-center gap-1 mt-0.5">
                              <Phone size={10} /> {it.customer_phone}
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditing({
                            id: it.id, plate: it.plate,
                            customer_name: "", customer_phone: "",
                          })}
                          className="text-xs text-muted-foreground italic hover:text-primary transition-colors"
                          data-testid={`plate-sub-add-customer-${it.plate}`}
                        >
                          + Sahip bilgisi ekle
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{it.plan_name}</TableCell>
                    <TableCell className="text-xs">{formatDateTime(it.start_date)}</TableCell>
                    <TableCell className="text-xs">{formatDateTime(it.end_date)}</TableCell>
                    <TableCell>
                      {active ? <Badge className="rounded-sm text-[10px] uppercase tracking-widest">Aktif</Badge>
                        : pending ? <Badge variant="secondary" className="rounded-sm text-[10px] uppercase tracking-widest">Beklemede</Badge>
                          : <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-widest">Pasif</Badge>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => setEditing({
                            id: it.id, plate: it.plate,
                            customer_name: it.customer_name || "",
                            customer_phone: it.customer_phone || "",
                          })}
                          data-testid={`plate-sub-edit-customer-btn-${it.plate}`}
                          title="Müşteri bilgisini düzenle"
                        >
                          <PencilSimple size={14} />
                        </Button>
                        {isSuperAdmin && (
                          <Button variant="ghost" size="sm" onClick={() => del(it.id)}>
                            <Trash size={14} className="text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Sahip Düzenle Modal */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PencilSimple size={16} /> Müşteri Bilgisi Düzenle
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Plaka</div>
                <Plate>{editing.plate}</Plate>
              </div>
              <div>
                <Label>Sahip Adı Soyadı</Label>
                <Input
                  autoFocus
                  value={editing.customer_name}
                  onChange={(e) => setEditing({ ...editing, customer_name: e.target.value })}
                  className="rounded-sm"
                  placeholder="Örn. Ahmet Yılmaz"
                  data-testid="plate-sub-edit-name-input"
                  onKeyDown={(e) => e.key === "Enter" && !editBusy && saveCustomerEdit()}
                />
              </div>
              <div>
                <Label>Telefon</Label>
                <Input
                  value={editing.customer_phone}
                  onChange={(e) => setEditing({ ...editing, customer_phone: e.target.value })}
                  className="rounded-sm font-mono"
                  placeholder="05XX XXX XX XX"
                  data-testid="plate-sub-edit-phone-input"
                  onKeyDown={(e) => e.key === "Enter" && !editBusy && saveCustomerEdit()}
                />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setEditing(null)} className="rounded-sm uppercase text-xs">
                  İptal
                </Button>
                <Button
                  onClick={saveCustomerEdit}
                  disabled={editBusy}
                  className="rounded-sm bg-primary uppercase text-xs font-semibold"
                  data-testid="plate-sub-edit-save-btn"
                >
                  {editBusy ? "Kaydediliyor..." : "Kaydet"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
